import { useField } from 'formik';
import React, { CSSProperties } from 'react';
import styles from './form-input.module.scss';

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
    label: string;
    name: string;
    placeholder?: string;
    limited?: boolean;
    limit?: number;
    style?: CSSProperties;
}

export const FormInput = (props: Props) => {
    const { className, style, limited, limit: customLimit, label, type = 'text', ...inputProps } = props;
    const fieldConfig = { type, ...inputProps };
    const [field, meta] = useField(fieldConfig);
    const limit = customLimit ?? (limited ? 45 : 240);

    const valueLength = typeof field.value === 'string'
        ? field.value.length
        : field.value
            ? String(field.value).length
            : 0;
    const lengthError = limited && valueLength > limit
        ? `Must be ${limit} characters or less`
        : null;

    const valueHasChanged = !Object.is(field.value, meta.initialValue);
    const errorMessage = lengthError ?? (valueHasChanged ? meta.error : null);
    const showError = Boolean(errorMessage);
    const showSuccess = Boolean(!errorMessage && !meta.error && valueHasChanged);
    const showLetterLimit = Boolean(limited && !showError);
    const shouldAddSpacing = showLetterLimit || showError;

    const inputClassName = [
        styles.input,
        showSuccess ? styles.inputSuccess : null,
        showError ? styles.inputError : null,
        shouldAddSpacing ? styles.inputSpacing : null,
        className,
    ].filter(Boolean).join(' ');

    return (
        <label htmlFor={inputProps.id || inputProps.name} className={styles.wrapper} style={style}>
            <span className={styles.fieldName}>{label}</span>
            {inputProps.required ? <span className={styles.asterisk}>*</span> : null}
            <input
                className={inputClassName}
                type={type}
                aria-invalid={showError ? true : showSuccess ? false : undefined}
                {...field}
                {...inputProps}
            />
            {showLetterLimit ? <div className={styles.letterLimit}>{valueLength} of {limit}</div> : null}
            {showError ? (<div className={styles.error}>{errorMessage}</div>) : null}
        </label>
    );
};
